import { use } from "react";

import type { PostType } from "@/contexts/feed-context";
import { UserContext } from "@/contexts/user-context";
import { PostByPostHashStatsDocument } from "@/graphql/codegen/graphql";
import { useOwnQuery } from "@/hooks/use-own-query";
import { AssociationReactionValue, AssociationType } from "@/utils/constants";

export const useGetCurrentUserPostStats = (post: PostType | null = null) => {
  if (!post?.postHash) {
    throw new Error("Post hash is required");
  }

  const { currentUserPublicKey } = use(UserContext);
  const currentUserFilter = {
    poster: {
      profile: {
        publicKey: {
          equalTo: currentUserPublicKey,
        },
      },
    },
  };

  const notHidden = {
    isHidden: {
      equalTo: false,
    },
  };

  const { data, loading, setOptimistic, fetchOnly, readLatestData } =
    useOwnQuery(PostByPostHashStatsDocument, {
      variables: {
        postHash: post.postHash,
        currentUserRepostsFilter: {
          body: {
            isNull: true,
          },
          ...notHidden,
          ...currentUserFilter,
        },
        currentUserQuotesFilter: {
          body: {
            isNull: false,
          },
          ...notHidden,
          ...currentUserFilter,
        },
        currentUserReactionsFilter: {
          associationType: {
            equalTo: AssociationType.reaction,
          },
          transactor: {
            publicKey: {
              equalTo: currentUserPublicKey,
            },
          },
        },
        currentUserDiamondsFilter: {
          associationType: {
            equalTo: AssociationType.diamond,
          },
          extraData: {
            contains: {
              SenderPublicKey: currentUserPublicKey,
            },
          },
        },
      },
    });

  const mergedPostStats = post.postStats ?? {};

  const {
    totalReactionCount = 0,
    positiveReactionCount = 0,
    negativeReactionCount = 0,

    likeReactionCount = 0,
    laughReactionCount = 0,
    loveReactionCount = 0,
    dislikeReactionCount = 0,
    angryReactionCount = 0,
    astonishedReactionCount = 0,
    sadReactionCount = 0,

    replyCount = 0,
    viewCount = 0,
    whaleViewCount = 0,

    quoteCount = 0,
    repostCount = 0,

    upvoteWealthTotalUsdCents = 0,
    upvoteWealthTotalUsdCentsCapped = 0,
  } = mergedPostStats;

  const diamondsTotalCount = mergedPostStats.diamondsTotalCount ?? 0;

  const diamondsTotalUsdCents = mergedPostStats.diamondsTotalUsdCents ?? 0;

  const reactionGroupCounts = {
    [AssociationReactionValue.ANGRY]: angryReactionCount,
    [AssociationReactionValue.SAD]: sadReactionCount,
    [AssociationReactionValue.LIKE]: likeReactionCount,
    [AssociationReactionValue.LOVE]: loveReactionCount,
    [AssociationReactionValue.LAUGH]: laughReactionCount,
    [AssociationReactionValue.DISLIKE]: dislikeReactionCount,
    [AssociationReactionValue.ASTONISHED]: astonishedReactionCount,
  };

  const sortedReactionGroupsCounts = Object.entries(reactionGroupCounts)
    .sort((a, b) => b[1]! - a[1]!)
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

  //const legacyLikesCount = Number(postStats?.likes?.totalCount) || 0;
  const legacyLikesCount = 0;

  const totalReactionsCount = totalReactionCount! + legacyLikesCount;

  const hasCurrentPostRepostedByCurrentUser =
    Number(
      data?.postByPostHash?.currentUserReposts?.nodes?.filter(
        (x) => !x?.isHidden,
      ).length,
    ) > 0;
  const hasCurrentPostQuotedByCurrentUser =
    Number(data?.postByPostHash?.currentUserQuotes?.nodes?.length) > 0;
  const hasCurrentUserReactions =
    Number(data?.postByPostHash?.currentUserReactions?.nodes?.length) > 0;
  const hasCurrentUserDiamonds =
    Number(data?.postByPostHash?.currentUserDiamonds?.nodes?.length) > 0;

  return {
    data,
    loading,
    setOptimistic,
    fetchOnly,
    readLatestData,

    sortedReactionGroupsCounts,
    reactionGroupCounts,
    legacyLikesCount,
    viewCount,
    whaleViewCount,
    quoteCount,
    replyCount,
    repostCount,
    totalReactionsCount,
    positiveReactionCount,
    negativeReactionCount,

    diamondsTotalCount,
    diamondsTotalUsdCents,
    diamondsTotalUsd: diamondsTotalUsdCents! / 100,

    upvoteWealthTotalUsdCents,
    upvoteWealthTotalUsdCentsCapped,

    hasCurrentPostRepostedByCurrentUser,
    hasCurrentPostQuotedByCurrentUser,
    currentUserRepost: data?.postByPostHash?.currentUserReposts?.nodes?.[0],
    hasCurrentUserReactions,
    hasCurrentUserDiamonds,
  };
};
